import { useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Clock, Users, Calendar, Tag, Phone, MessageCircle } from "lucide-react";
import { getClub, getSchool } from "@/data/clubs";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ChessManifesto } from "@/components/ChessManifesto";
import { RegistrationForm } from "@/components/RegistrationForm";
import { Reveal } from "@/components/Reveal";
import { useScrollTo } from "@/lib/scroll";
import { waHref } from "@/lib/contact";

export default function ClubDetail() {
  const { school: schoolId, id } = useParams();
  const navigate = useNavigate();
  const schoolData = getSchool(schoolId);
  const club = getClub(schoolData.id, id);
  const scrollTo = useScrollTo();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (club) document.title = `${club.name} | ${schoolData.label}`;
  }, [id, club, schoolData.label]);

  if (!club) {
    return (
      <main className="min-h-screen grid place-items-center">
        <div className="text-center">
          <p className="font-head font-black text-3xl">החוג לא נמצא</p>
          <Link to="/" className="mt-4 inline-block text-accent link-underline">חזרה לעמוד הבית</Link>
        </div>
      </main>
    );
  }

  const others = schoolData.clubs.filter((c) => c.id !== club.id).slice(0, 3);

  return (
    <main data-testid={`club-page-${club.id}`}>
      <Navbar />

      <section ref={ref} className="relative h-[70vh] min-h-[520px] overflow-hidden">
        <motion.img style={{ y }} src={club.image} alt={club.name} className="absolute inset-0 w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/20" />
        <div className="relative h-full max-w-7xl mx-auto px-5 md:px-10 flex flex-col justify-end pb-14 text-paper">
          <button onClick={() => navigate(`/${schoolData.id}`)} className="flex items-center gap-2 text-white/80 hover:text-white mb-8 self-start" data-testid="back-home">
            <ArrowRight className="w-4 h-4" /> חזרה
          </button>
          <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-head font-semibold text-white/70 mb-3">
            {club.field}
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }} className="font-head font-black text-6xl md:text-8xl tracking-tighter">
            {club.name}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mt-4 text-xl text-white/80 max-w-xl">
            {club.tagline}
          </motion.p>
        </div>
      </section>

      <section className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-head font-black text-3xl md:text-4xl mb-6">על החוג</h2>
              <p className="text-lg md:text-xl text-ink/70 leading-relaxed whitespace-pre-line">{club.long}</p>
            </Reveal>
            {club.poster && (
              <Reveal delay={0.1}>
                <img
                  src={club.poster}
                  alt={`פרסום חוג ${club.name}`}
                  className="mt-10 w-full max-w-xl rounded-2xl border border-ink/10 shadow-lg"
                  data-testid="club-poster"
                />
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-ink/10 bg-white p-7 md:p-8 sticky top-24">
                <div className="flex items-center gap-3 pb-5 border-b border-ink/10">
                  {club.instructorImage ? (
                    <img
                      src={club.instructorImage}
                      alt={club.instructor}
                      className="w-12 h-12 rounded-full object-cover grayscale"
                      data-testid="instructor-image"
                    />
                  ) : (
                    <Users className="w-5 h-5 text-accent" />
                  )}
                  <div>
                    <div className="text-xs text-ink/50">מדריך</div>
                    <div className="font-head font-bold">{club.instructor}</div>
                  </div>
                </div>

                {club.instructorPhone && (
                  <a
                    href={waHref(club.instructorPhone, `היי, אשמח לפרטים על חוג ${club.name}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 flex items-center justify-center gap-2 w-full font-head font-semibold border border-accent text-accent py-3 rounded-xl hover:bg-accent hover:text-white transition-colors"
                    data-testid="instructor-contact"
                  >
                    <Phone className="w-4 h-4" />
                    וואטסאפ עם המדריך
                    <span className="tabular-nums" dir="ltr">{club.instructorPhone}</span>
                  </a>
                )}

                {club.whatsappGroup && (
                  <a
                    href={club.whatsappGroup}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 flex items-center justify-center gap-2 w-full font-head font-semibold bg-[#25D366] text-white py-3 rounded-xl hover:bg-[#1eb955] transition-colors"
                    data-testid="whatsapp-group-link"
                  >
                    <MessageCircle className="w-4 h-4" />
                    הצטרפות לקבוצת הוואטסאפ של החוג
                  </a>
                )}

                <div className="py-5 space-y-4">
                  <div className="text-xs text-ink/50 font-semibold">מפגשים</div>
                  {club.schedule.length === 0 && (
                    <div className="rounded-xl bg-ink/[0.03] p-4 font-head font-bold text-ink/60" data-testid="schedule-note">
                      {club.scheduleNote || "פרטים בהמשך"}
                    </div>
                  )}
                  {club.schedule.map((s, i) => (
                    <div key={i} className="rounded-xl bg-ink/[0.03] p-4">
                      <div className="flex items-center gap-2 font-head font-bold mb-2">
                        <Users className="w-4 h-4 text-accent" /> כיתות {s.grades}
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink/70">
                        <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> יום {s.day}</span>
                        <span className="flex items-center gap-1.5" dir="ltr"><Clock className="w-4 h-4" /> {s.time}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-5 border-t border-ink/10">
                  <span className="flex items-center gap-2 text-ink/60"><Tag className="w-4 h-4" /> מחיר לחודש</span>
                  <span className="font-head font-black text-2xl text-accent">{club.price} ₪</span>
                </div>

                <a
                  href={schoolData.regUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 w-full flex items-center justify-center font-head font-semibold bg-accent text-white py-3.5 rounded-xl hover:bg-accent-600 transition-colors"
                  data-testid="detail-council-register"
                >
                  להרשמה באתר המועצה ←
                </a>
                <button
                  onClick={() => scrollTo("register")}
                  className="mt-3 w-full font-head font-semibold bg-ink text-paper py-3.5 rounded-xl hover:bg-accent transition-colors"
                  data-testid="detail-register-cta"
                >
                  השארת פרטים / שיעור ניסיון
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {club.id === "chess" && <ChessManifesto />}

      <RegistrationForm defaultClubs={[club.name]} schoolClubs={schoolData.clubs} schoolName={schoolData.name} />

      <section className="px-5 md:px-10 pb-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-head font-black text-3xl md:text-4xl mb-8">חוגים נוספים</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {others.map((c) => (
              <Link key={c.id} to={`/${schoolData.id}/club/${c.id}`} className="group relative rounded-2xl overflow-hidden aspect-[4/3] border border-ink/10" data-testid={`related-${c.id}`}>
                <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
                <div className="absolute bottom-4 right-4 text-paper font-head font-extrabold text-2xl">{c.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
